import { useNavigate } from 'react-router-dom';
import { userAppContext } from '../../context/Context';
import { useAcademicApplication } from '../../hooks/useAcademicApplication';
import { useCheckoutFlow } from '../../hooks/useCheckoutFlow';
import Tooltip from '../UI/Tooltip';
import { UserContext } from '../../types';
import { PricingCardProps, ButtonConfig } from './types';
import SharedButton from '@/global/Button/Button';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { slugifyTrackingValue, trackEvent } from '@/utils/analytics';

export function PricingCard({
  title,
  info,
  price,
  priceMo,
  included,
  details,
  onAcademicApply,
  onSeeMore,
  isCommercial: isCommercialProp,
  isUserDataLoading = false,
}: PricingCardProps) {
  const navigate = useNavigate();
  const { groups, user, isAuthenticated } = userAppContext() as unknown as UserContext;
  const {
    trialType,
    canSeePrices: canSeePricesFromHook,
    isCommercial: isCommercialFromHook,
  } = useAcademicApplication(user?.email, isAuthenticated);
  const { redirectToCheckout } = useCheckoutFlow();

  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademicPlan = groups?.includes('ROLE_ACADEMIC');
  const isStandardPlan = groups?.includes('ROLE_STANDARD');
  const isAdvancedPlan = groups?.includes('ROLE_PROFESSIONAL');
  const isUnlimitedPlan = groups?.includes('ROLE_UNLIMITED');

  // Use tag-based logic from hook
  const isCommercial = isCommercialProp ?? (isTrial ? trialType === 'commercial' : isCommercialFromHook());
  const canSeePrices = canSeePricesFromHook();

  // For authenticated Standard/Academic users without pricing access, treat as commercial
  const shouldShowContactUs =
    !isUserDataLoading && isAuthenticated && (isStandardPlan || isAcademicPlan) && !canSeePrices;

  const isCurrentPlan =
    (title === 'Academic' && isAcademicPlan) ||
    (title === 'Standard' && isStandardPlan) ||
    (title === 'Advanced' && isAdvancedPlan) ||
    (title === 'Premium' && isUnlimitedPlan);

  const getButtonConfig = (): ButtonConfig => {
    if (!isAuthenticated) {
      if (title === 'Standard' || title === 'Premium') {
        return { show: true, text: 'Contact Us' };
      }
      if (title === 'Academic') {
        return { show: true, text: 'Apply for Free Academic Plan' };
      }
      return { show: true, text: 'Try it out' };
    }

    if (isCurrentPlan) {
      return { showTag: true, variant: 'primary' };
    }

    // Users not in allowed lists should contact us
    if (shouldShowContactUs) {
      return { show: true, text: 'Contact Us' };
    }

    if (isCommercial) {
      return { show: true, text: 'Contact Us' };
    }

    if (isTrial) {
      if (title === 'Academic') {
        return { show: true, text: 'Apply for Free Academic Plan', isAcademicApply: true };
      }
      if (title === 'Standard' || title === 'Advanced') {
        return {
          show: true,
          text: `Get ${title}`,
          disabled: true,
          tooltip: 'Apply for the Free Academic Plan to access paid plans',
        };
      }
      return { show: true, text: 'Contact Us' };
    }

    if (isAcademicPlan && (title === 'Standard' || title === 'Advanced')) {
      return { show: true, text: `Get ${title}`, isStripeUpgrade: true };
    }

    if (isStandardPlan) {
      if (title === 'Academic')
        return { show: true, text: 'Apply for Free Academic Plan', isAcademicApply: true };
      if (title === 'Advanced') return { show: true, text: 'Get Advanced', isStripeUpgrade: true };
    }

    if (isAdvancedPlan) {
      if (title === 'Academic')
        return { show: true, text: 'Apply for Free Academic Plan', isAcademicApply: true };
      if (title === 'Standard') return { show: true, text: 'Get Standard', isStripeUpgrade: true };
    }

    if (title === 'Premium') {
      return { show: true, text: 'Contact Us' };
    }

    if (title === 'Advanced') {
      return { show: true, text: 'Try it out' };
    }

    return { show: true, text: 'Try it out' };
  };

  const buttonConfig = getButtonConfig();

  const handleButtonClick = () => {
    const cardId = slugifyTrackingValue(title);
    const actionLabel = isCommercial || shouldShowContactUs ? 'Contact us' : buttonConfig.text || 'action';
    trackEvent({
      name: 'cta_click',
      buttonId: `pricing_${cardId}_${slugifyTrackingValue(actionLabel)}`,
      label: actionLabel,
      page: 'plans',
      section: 'pricing_card',
      feature: 'pricing',
      eventCategory: 'pricing',
      additionalParams: { pricing_plan: title },
    });

    // If button says "Contact Us", always go to Support
    if (buttonConfig.text === 'Contact Us' || isCommercial || shouldShowContactUs) {
      navigate('/Support');
      return;
    }

    if (buttonConfig.isAcademicApply) {
      onAcademicApply();
    } else if (buttonConfig.isStripeUpgrade) {
      const priceId =
        title === 'Standard'
          ? process.env.REACT_APP_STRIPE_STANDARD_PRICE_ID || 'price_1SCebx2Ml6stFA0rHcXCPwNj'
          : process.env.REACT_APP_STRIPE_ADVANCED_PRICE_ID || 'price_1SPPcK2Ml6stFA0rOiXzsUUM';
      redirectToCheckout({ title, priceId });
    } else {
      if (title === 'Standard' || title === 'Premium') {
        navigate('/Support');
      } else {
        const routes: Record<string, string> = {
          Academic: '/academic-apply',
          Advanced: '/free-trial-apply',
        };
        navigate(routes[title] || '/Register');
      }
    }
  };

  const isAdvancedCard = title === 'Advanced';
  const buttonVariant = isAdvancedCard ? 'primary' : 'secondary';

  const renderButton = () => {
    if (isUserDataLoading && isAuthenticated) {
      return (
        <SharedButton
          variant={buttonVariant}
          size="md"
          disabled
          className={`mx-0 ${isAdvancedCard ? 'card-btn-advanced' : ''}`}
          style={{ width: '100%', opacity: 0.7, cursor: 'not-allowed' }}
        >
          Loading...
        </SharedButton>
      );
    }

    if (buttonConfig.showTag) {
      return (
        <SharedButton
          variant={buttonVariant}
          size="md"
          disabled
          style={{ cursor: 'not-allowed', width: '100%', opacity: 0.5 }}
        >
          Current Plan
        </SharedButton>
      );
    }

    if (!buttonConfig.show) return null;

    const buttonElement = (
      <SharedButton
        variant={buttonVariant}
        size="md"
        className={`mx-0 ${isAdvancedCard ? 'card-btn-advanced' : ''}`}
        onClick={handleButtonClick}
        disabled={buttonConfig.disabled}
        style={{
          ...(buttonConfig.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
          width: '100%',
        }}
      >
        {isCommercial || shouldShowContactUs ? 'Contact us' : buttonConfig.text}
      </SharedButton>
    );

    return buttonConfig.tooltip ? (
      <Tooltip content={buttonConfig.tooltip} placement="top" allowCopy={false}>
        {buttonElement}
      </Tooltip>
    ) : (
      buttonElement
    );
  };

  const showPriceLoader = isAuthenticated && isUserDataLoading && title !== 'Academic' && !!price;
  const showPrices =
    price && isAuthenticated && !isCommercial && !isTrial && canSeePrices && !shouldShowContactUs;
  const showTrialMessage = isTrial && !isCommercial && (title === 'Standard' || title === 'Advanced');
  const showContactForPrice =
    (!isUserDataLoading &&
      (!isAuthenticated || (!showPrices && !showTrialMessage)) &&
      title !== 'Academic') ||
    shouldShowContactUs;
  const showAcademicFree = title === 'Academic' && (!showPrices || !isAuthenticated);
  const plansWithMcp = title === 'Academic' || title === 'Advanced' || title === 'Premium';

  const getDetailLabel = (label: string) => {
    if (!plansWithMcp || label.includes('MCP') || !label.includes('REST API')) return label;
    return label.replace('REST API,', 'REST API, MCP,');
  };

  return (
    <div
      className={`text-left mb-0 PricingCard col-12 col-sm-6 col-lg-3 ${title === 'Advanced' ? 'PricingCard-advanced border-2 border-solid' : ''} ${title === 'Premium' ? 'PricingCard-premium' : ''}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div>
        {info && (
          <div
            style={{
              position: 'absolute',
              top: '25px',
              right: '-40px',
              backgroundColor: 'var(--blue-color',
              color: 'white',
              padding: '5px 40px',
              transform: 'rotate(45deg)',
              fontSize: '12px',
              fontWeight: 'bold',
              zIndex: 1,
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            Most Popular
          </div>
        )}

        {/* Title */}
        <div>
          <p className="font-medium text-2xl">{title}</p>
        </div>

        {/* Price */}
        <div>
          {showPriceLoader && (
            <div className="h-12 grid items-center justify-start animate-pulse">
              <div className="h-6 w-28 rounded bg-pink-50 mb-2" />
              <div className="h-4 w-36 rounded bg-pink-100" />
            </div>
          )}
          {showPrices && (
            <>
              <div className="h-12 grid items-center justify-start">
                {title === 'Academic' ? (
                  <div>
                    <span className="font-bold text-xl">{price}</span>
                  </div>
                ) : priceMo ? (
                  <div>
                    <span className="font-bold text-xl">{priceMo}</span>
                    <small>€/mo</small>
                  </div>
                ) : null}
                {title !== 'Academic' && (
                  <span>
                    <small>
                      ({price}
                      {price !== 'Free' ? '€ billed yearly' : ''})
                    </small>
                  </span>
                )}
              </div>
            </>
          )}
          {showAcademicFree && (
            <div className="h-12 grid items-center justify-start">
              <div>
                <span className="font-bold text-lg">Free</span>
              </div>
            </div>
          )}
          {showTrialMessage && (
            <div className="h-12 grid items-center justify-start">
              <p className="text-xl font-semibold">Requires academic approval</p>
            </div>
          )}
          {showContactForPrice && (
            <div className="h-12 grid items-center justify-start">
              <span className="text-lg font-semibold" style={{ opacity: 0.8 }}>
                Contact us for pricing
              </span>
            </div>
          )}
        </div>

        {/* Text */}
        <div style={{ minHeight: '60px', maxHeight: '60px' }}>
          <p className="card-price text-xs mt-2">{included}</p>
        </div>

        {/* Button */}
        <div className="flex justify-center">{renderButton()}</div>

        {/* Text */}
        <div style={{ minHeight: '80px' }}>
          {details && (
            <ul className="mt-2">
              {details.map((detail, index) => (
                <li key={index} className="flex items-center gap-2 p-2">
                  <span>{detail.icon}</span>
                  <span className="text-xs">{getDetailLabel(detail.label)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div>
        <button
          type="button"
          onClick={() => {
            trackEvent({
              name: 'expand_details',
              buttonId: `pricing_${slugifyTrackingValue(title)}_see_more`,
              label: 'See more',
              page: 'plans',
              section: 'pricing_card',
              feature: 'pricing',
              eventCategory: 'pricing',
              additionalParams: { pricing_plan: title },
            });
            onSeeMore();
          }}
          className="text-blue font-bold flex items-center gap-2 text-left justify-start ml-2"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          <PlusCircleIcon className="w-5 h-5 text-blue" />
          See more
        </button>
      </div>
    </div>
  );
}
